// Animiertes Umsortieren filterbarer Kacheln ([data-filter-item]) per FLIP.
// Ablauf eines Filterwechsels (alles gleichzeitig, kein Layout-Sprung):
//   - ausscheidende Kacheln werden an ihrer alten Stelle absolut positioniert und
//     faden aus (geben den Platz im Fluss sofort frei),
//   - bleibende Kacheln gleiten per FLIP weich von alter zu neuer Position,
//   - neue Kacheln faden ein.
// Dynamische Werte (Versatz/Position) laufen über CSS-Custom-Properties, die
// eigentlichen Style-Regeln stehen im Stylesheet (_filter.scss) -> §8-konform.
// prefers-reduced-motion (§16) -> sofortiges Umschalten ohne Animation.
const reduceMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const MOVE_MS = 450; // Sicherheits-Fallback > längste CSS-Dauer

// Ruft cb einmalig auf: beim passenden Event ODER nach MOVE_MS (falls das Event
// ausbleibt, z. B. wenn der Tab im Hintergrund gedrosselt wird).
function once(el, type, matches, cb) {
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    el.removeEventListener(type, handler);
    window.clearTimeout(timer);
    cb();
  };
  const handler = (event) => {
    if (event.target === el && matches(event)) finish();
  };
  el.addEventListener(type, handler);
  const timer = window.setTimeout(finish, MOVE_MS);
}

function clearFlip(item) {
  item.classList.remove("is-flip-invert", "is-flip-play");
  item.style.removeProperty("--flip-x");
  item.style.removeProperty("--flip-y");
}

function clearLeave(item) {
  item.classList.remove("is-leaving");
  item.style.removeProperty("--leave-x");
  item.style.removeProperty("--leave-y");
  item.style.removeProperty("--leave-w");
}

// items: Liste der [data-filter-item]; isVisible(item) -> soll sichtbar sein?
export function animateFilter(itemList, isVisible) {
  const items = [...itemList];
  if (!items.length) return;

  if (reduceMotion) {
    for (const item of items) {
      clearFlip(item);
      clearLeave(item);
      item.hidden = !isVisible(item);
    }
    return;
  }

  // Laufende Bewegungen abschließen, damit die Messungen stimmen.
  for (const item of items) {
    clearFlip(item);
    if (item.classList.contains("is-leaving") && isVisible(item)) {
      clearLeave(item);
      item.hidden = false; // wieder in den Fluss holen
    }
  }

  const container = items[0].parentElement;
  const cRect = container.getBoundingClientRect();

  // FIRST: Positionen der aktuell im Fluss liegenden (sichtbaren) Kacheln.
  const first = new Map();
  for (const item of items) {
    if (!item.hidden && !item.classList.contains("is-leaving")) {
      first.set(item, item.getBoundingClientRect());
    }
  }

  // Zielzustand anwenden: neue einblenden, ausscheidende aus dem Fluss nehmen.
  const entering = [];
  for (const item of items) {
    const show = isVisible(item);
    if (show) {
      if (item.hidden) {
        item.hidden = false;
        entering.push(item);
      }
    } else if (first.has(item)) {
      const r = first.get(item);
      first.delete(item); // belegt den Fluss nicht mehr
      item.style.setProperty("--leave-x", `${r.left - cRect.left - container.clientLeft}px`);
      item.style.setProperty("--leave-y", `${r.top - cRect.top - container.clientTop}px`);
      item.style.setProperty("--leave-w", `${r.width}px`);
      item.classList.add("is-leaving");
      once(item, "animationend", (e) => e.animationName === "iba-filter-out", () => {
        item.hidden = true;
        clearLeave(item);
      });
    } else if (!item.hidden) {
      item.hidden = true;
    }
  }

  // LAST + INVERT: bleibende Kacheln, die sich verschoben haben.
  const flippers = [];
  for (const item of first.keys()) {
    if (item.hidden) continue;
    const prev = first.get(item);
    const now = item.getBoundingClientRect();
    const dx = prev.left - now.left;
    const dy = prev.top - now.top;
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
    item.style.setProperty("--flip-x", `${dx}px`);
    item.style.setProperty("--flip-y", `${dy}px`);
    item.classList.add("is-flip-invert"); // optisch zurück auf alte Position
    flippers.push(item);
  }

  // Reflow erzwingen, damit der invertierte Zustand greift, dann PLAY.
  void container.offsetWidth;

  for (const item of flippers) {
    item.classList.remove("is-flip-invert");
    item.classList.add("is-flip-play"); // gleitet animiert nach translate(0,0)
    once(item, "transitionend", (e) => e.propertyName === "transform", () => clearFlip(item));
  }

  for (const item of entering) {
    item.classList.add("is-entering");
    once(item, "animationend", (e) => e.animationName === "iba-filter-in", () =>
      item.classList.remove("is-entering"),
    );
  }
}
